// Each pair negotiates three fixed slots once: microphone, camera, screen.
// Later device changes replace tracks without adding competing SDP offers.
import { MeetingDiagnostics, createMeetLogger, iceServerSummary, candidateSummary, errorSummary } from './MeetingDiagnostics.js';

export class MeetingConnection {
  constructor(session, url, callbacks) {
    this.session = session;
    this.callbacks = callbacks;
    this.peers = new Map();
    this.tracks = [null, null, null];
    this.closed = false;
    this.log = createMeetLogger();
    this.peerNumber = 0;
    this.log('session.config', { version: 1, policy: session.iceTransportPolicy || 'all', iceServers: iceServerSummary(session.iceServers) });
    if (!(session.iceServers || []).some(server => (Array.isArray(server.urls) ? server.urls : [server.urls]).some(value => /^turns?:/.test(value)))) {
      this.log('session.no-turn', { hint: 'Backend supplied no TURN server; check its environment and recreate backend' }, 'warn');
    }
    this.ws = new WebSocket(url);
    this.queue = Promise.resolve();
    this.ws.onopen = () => {
      this.log('signaling.open');
      this.send({
        type: 'join',
        token: session.token
      });
    };
    this.ws.onmessage = event => {
      this.queue = this.queue.then(() => this.receive(JSON.parse(event.data))).catch(error => {
        this.log('signaling.receive.error', errorSummary(error), 'error');
        if (!this.closed) callbacks.error('Не удалось установить соединение. Выйдите и войдите снова.');
      });
    };
    this.ws.onclose = event => {
      this.log('signaling.closed', { code: event.code, wasClean: event.wasClean, intentional: this.closed });
      if (this.closed) return;
      const messages = {
        4005: 'Организатор удалил вас из встречи.',
        4006: 'Организатор завершил встречу.',
        4007: 'Встреча удалена или пароль изменён.',
        4009: 'Сервер временно не может проверить доступ. Попробуйте войти снова немного позже.'
      };
      this.close();
      callbacks.disconnected(messages[event.code] || 'Соединение прервано. Войдите снова для восстановления связи.');
    };
    this.ws.onerror = () => {
      this.log('signaling.error', { readyState: this.ws.readyState }, 'error');
      callbacks.error('Сервер встречи недоступен. Проверьте подключение.');
    };
    this.joinTimeout = setTimeout(() => {
      if (!this.id) {
        this.log('signaling.join.timeout', {}, 'error');
        this.close();
        callbacks.disconnected('Сервер не ответил. Попробуйте войти ещё раз.');
      }
    }, 15000);
  }
  send(data) {
    if (data.type === 'signal') this.log('signal.send', { peer: this.peers.get(data.to)?.label,
      description: data.description?.type, candidate: data.candidate ? candidateSummary(data.candidate) : undefined });
    if (this.ws.readyState === 1) this.ws.send(JSON.stringify(data));
    else this.log('signaling.send.dropped', { type: data.type, readyState: this.ws.readyState }, 'warn');
  }
  async operation(peer, stage, action) {
    this.log(stage + '.start', { peer: peer.label, signaling: peer.pc.signalingState });
    try {
      const result = await action();
      this.log(stage + '.ok', { peer: peer.label, signaling: peer.pc.signalingState });
      return result;
    } catch (error) {
      this.log(stage + '.error', { peer: peer.label, ...errorSummary(error) }, 'error');
      throw error;
    }
  }
  update() {
    if (!this.closed) this.callbacks.peers([...this.peers.values()].map(p => ({
      ...p.info,
      stream: p.stream,
      screenStream: p.screenStream,
      status: p.pc.connectionState
    })));
  }
  channel(peer, channel) {
    peer.channel = channel;
    channel.onopen = () => { this.log('datachannel.open', { peer: peer.label }); this.update(); };
    channel.onclose = () => this.log('datachannel.closed', { peer: peer.label });
    channel.onerror = () => this.log('datachannel.error', { peer: peer.label, readyState: channel.readyState }, 'warn');
    channel.onmessage = event => {
      if (typeof event.data !== 'string' || event.data.length > 8000) return;
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'chat' && typeof data.text === 'string' && data.text.trim()) this.callbacks.chat({
          id: `${peer.info.id}-${Date.now()}-${Math.random()}`,
          name: peer.info.name,
          text: data.text.slice(0, 2000),
          at: Date.now()
        });
      } catch {/* Invalid peer payloads are ignored. */}
    };
  }
  makePeer(info, initiator) {
    const pc = new RTCPeerConnection({
      iceServers: this.session.iceServers,
      iceTransportPolicy: this.session.iceTransportPolicy || 'all'
    });
    const peer = {
      info,
      pc,
      initiator,
      candidates: [],
      stream: new MediaStream(),
      screenStream: new MediaStream(),
      retries: 0
    };
    peer.label = `P${++this.peerNumber}`;
    peer.diagnostics = new MeetingDiagnostics(pc, this.log, peer.label);
    this.log('peer.created', { peer: peer.label, initiator });
    this.peers.set(info.id, peer);
    pc.onicecandidate = e => {
      if (e.candidate) this.send({
        type: 'signal',
        to: info.id,
        candidate: e.candidate
      });
    };
    pc.ontrack = e => {
      const slot = pc.getTransceivers().indexOf(e.transceiver);
      const stream = slot === 2 ? peer.screenStream : peer.stream;
      stream.addTrack(e.track);
      e.track.onunmute = () => this.update();
      this.update();
    };
    pc.onconnectionstatechange = () => {
      this.update();
      if (pc.connectionState === 'connected') {
        clearTimeout(peer.retryTimer);
        peer.retryTimer = null;
        peer.retries = 0;
        peer.recoveryStarted = undefined;
        peer.recoveryReported = false;
      }
      if (pc.connectionState !== 'connected') this.scheduleRecovery(peer, 3000);
    };
    // Also recover when the browser stays in new/connecting without a failure event.
    this.scheduleRecovery(peer, 25000);

    pc.ondatachannel = e => this.channel(peer, e.channel);
    if (initiator) {
      ['audio', 'video', 'video'].forEach(kind => pc.addTransceiver(kind, {
        direction: 'sendrecv'
      }));
      this.channel(peer, pc.createDataChannel('chat', {
        ordered: true
      }));
    }
    this.update();
    return peer;
  }
  scheduleRecovery(peer, delay) {
    if (this.closed || !peer.initiator || peer.retryTimer || peer.pc.connectionState === 'closed') return;
    peer.recoveryStarted ??= Date.now();
    peer.retryTimer = setTimeout(() => {
      peer.retryTimer = null;
      // Share the signaling queue with incoming SDP to avoid competing operations.
      this.queue = this.queue.then(async () => {
        if (this.closed || this.peers.get(peer.info.id) !== peer || peer.pc.connectionState === 'connected') return;
        if (peer.retries >= 2 || Date.now() - peer.recoveryStarted > 90000) {
          if (!peer.recoveryReported) {
            peer.recoveryReported = true;
            this.callbacks.error('Не удалось восстановить связь с участником. Проверьте интернет и войдите во встречу заново.');
          }
          return;
        }
        if (peer.pc.signalingState !== 'stable') {
          this.scheduleRecovery(peer, 3000);
          return;
        }
        peer.retries++;
        try {
          await this.operation(peer, 'ice.restart', async () => peer.pc.setLocalDescription(await peer.pc.createOffer({ iceRestart: true })));
          this.send({ type: 'signal', to: peer.info.id, description: peer.pc.localDescription });
        } catch { /* operation logs structured diagnostic details */ }
        this.scheduleRecovery(peer, 20000);
      }).catch(error => this.log('ice.recovery.error', errorSummary(error), 'error'));
    }, delay);
  }
  async attach(peer) {
    await this.operation(peer, 'media.attach', () => this.syncTracks(peer));
  }
  syncTracks(peer) {
    // Read devices when each queued replacement starts, including late joiners.
    peer.trackQueue = (peer.trackQueue || Promise.resolve()).catch(() => {}).then(() => {
      if (this.closed || this.peers.get(peer.info.id) !== peer) return;
      return Promise.all(peer.pc.getTransceivers().map((t, i) => {
        t.direction = 'sendrecv';
        return t.sender.replaceTrack(this.tracks[i] || null);
      }));
    });
    return peer.trackQueue;
  }
  async receive(msg) {
    if (this.closed) return;
    if (msg.type === 'error') {
      this.log('signaling.server.error', {}, 'error');
      this.callbacks.error(msg.message);
      return;
    }
    if (msg.type === 'joined') {
      clearTimeout(this.joinTimeout);
      this.id = msg.yourId;
      this.log('signaling.joined', { participants: msg.participants.length });
      this.callbacks.connected();
      this.mediaState();
      for (const info of msg.participants) {
        const peer = this.makePeer(info, true);
        await this.attach(peer);
        await this.operation(peer, 'sdp.local.offer', async () => peer.pc.setLocalDescription(await peer.pc.createOffer()));
        this.send({
          type: 'signal',
          to: info.id,
          description: peer.pc.localDescription
        });
      }
    } else if (msg.type === 'user-joined') {
      this.makePeer(msg, false);
    } else if (msg.type === 'user-left') {
      const peer = this.peers.get(msg.id);
      if (peer) {
        this.log('peer.left', { peer: peer.label });
        peer.diagnostics.close();
        clearTimeout(peer.retryTimer);
        peer.pc.close();
        this.peers.delete(msg.id);
        this.update();
      }
    } else if (msg.type === 'media-state') {
      const peer = this.peers.get(msg.from);
      if (peer) {
        peer.info.media = msg.media;
        this.update();
      }
    } else if (msg.type === 'signal') {
      const peer = this.peers.get(msg.from);
      if (!peer) { this.log('signal.unknown-peer', {}, 'warn'); return; }
      if (msg.description) {
        this.log('signal.description.received', { peer: peer.label, type: msg.description.type });
        await this.operation(peer, 'sdp.remote', () => peer.pc.setRemoteDescription(msg.description));
        if (msg.description.type === 'offer') {
          await this.attach(peer);
          await this.operation(peer, 'sdp.local.answer', async () => peer.pc.setLocalDescription(await peer.pc.createAnswer()));
          this.send({
            type: 'signal',
            to: msg.from,
            description: peer.pc.localDescription
          });
        }
        for (const candidate of peer.candidates.splice(0)) await this.operation(peer, 'ice.remote.apply-queued', () => peer.pc.addIceCandidate(candidate));
      } else if (msg.candidate) {
        this.log('ice.remote.received', { peer: peer.label, queued: !peer.pc.remoteDescription, ...candidateSummary(msg.candidate) });
        if (peer.pc.remoteDescription) await this.operation(peer, 'ice.remote.apply', () => peer.pc.addIceCandidate(msg.candidate));else peer.candidates.push(msg.candidate);
      }
    }
  }
  mediaState() {
    this.send({
      type: 'media-state',
      audio: !!this.tracks[0],
      video: !!this.tracks[1],
      screen: !!this.tracks[2]
    });
  }
  async setTrack(slot, track) {
    if (this.closed) {
      track?.stop();
      return;
    }
    const previous = this.tracks[slot];
    this.tracks[slot] = track;
    const results = await Promise.allSettled([...this.peers.values()].map(p => this.syncTracks(p)));
    if (previous !== track) previous?.stop();
    if (this.closed) return;
    this.log('media.local.changed', { slot, enabled: !!track, readyState: track?.readyState, failedPeers: results.filter(result => result.status === 'rejected').length });
    this.mediaState();
    if (results.some(result => result.status === 'rejected')) this.callbacks.error('Не удалось обновить устройство для одного из участников. Перезайдите во встречу.');
  }
  chat(text) {
    let delivered = 0;
    for (const p of this.peers.values()) if (p.channel?.readyState === 'open' && p.channel.bufferedAmount < 65536) {
      p.channel.send(JSON.stringify({
        type: 'chat',
        text
      }));
      delivered++;
    }
    return delivered;
  }
  close() {
    this.log('session.close');
    this.closed = true;
    clearTimeout(this.joinTimeout);
    this.ws.close();
    this.tracks.forEach(t => t?.stop());
    for (const p of this.peers.values()) {
      p.diagnostics.close();
      clearTimeout(p.retryTimer);
      p.pc.close();
    }
    this.peers.clear();
  }
}
